import { Box, Hint, Section, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'
import {
  GetTrainingsSettingsDocument,
  GetTrainingsSettingsQuery,
} from 'generated/resources/graphql'

import { Company, useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ApprovalsBlock from 'modules/approvals/components/ApprovalsConfigurationBlock'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useGetKeys } from 'modules/trainings/hooks/EmptyPages/useGetKeys'
import { useCreateSetting } from 'modules/trainings/hooks/Settings/useCreateSetting'
import { useUpdateSetting } from 'modules/trainings/hooks/Settings/useUpdateSetting'

import { FundaeSubsidySection } from './FundaeSubsidySection'

type TrainingsSetting = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<GetTrainingsSettingsQuery['trainings']>['settingsConnection']
    >['nodes'][0]
  >
>

interface NewTrainingSettingFormValues {
  relatorioEnabled: boolean
  trainingRequestsEnabled: boolean
}

export const GlobalSettings = () => {
  const { company } = useCurrentSession()

  // TODO: Move this to the page query

  const { loading, data, error } = useQuery(GetTrainingsSettingsDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const setting = data?.trainings.settingsConnection.nodes?.at(0)
  const manageSettings = !!data?.trainings._policies.settingsManageSettings.can

  return (
    <ResponsivePage.Body variant="full" showHeader={false} error={error} loading={loading}>
      <Content company={company} manageSettings={manageSettings} setting={setting} />
    </ResponsivePage.Body>
  )
}

// This is neeeded to prevent the form from being created before the loading is done
function Content({
  company,
  manageSettings,
  setting,
}: {
  company: Company
  manageSettings: boolean
  setting?: TrainingsSetting
}) {
  const session = useCurrentSession()
  const { getKey, setKey } = useGetKeys()
  const { create } = useCreateSetting()
  const { update } = useUpdateSetting()
  const hidden = !!getKey('trainings-settings-hint')

  const form = useForm<NewTrainingSettingFormValues>({
    defaultValues: {
      relatorioEnabled: !!setting?.relatorioEnabled,
      trainingRequestsEnabled: !!setting?.trainingRequestsEnabled,
    },
    onSubmit: async (data) => {
      if (setting) {
        const result = await update({
          ...data,
          id: setting.id,
          companyId: company.id,
        })
        const handled = handleResult(result, () => {
          // TODO: do something with form errors
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: 'Training setting updated',
          })
          const values = form.values
          form.resetForm({ values })
        }
        return handled
      } else {
        const result = await create({ ...data, companyId: company.id })
        const handled = handleResult(result, () => {
          // TODO: do something with form errors
        })
        if (handled.success) {
          app.setFlash({
            type: 'correct',
            message: 'Training setting created',
          })

          const values = form.values
          form.resetForm({ values })
        }
        return handled
      }
    },
  })

  return (
    <Hint.Popover.Provider steps={2} onClose={() => setKey('trainings-settings-hint')}>
      <Section.TwoThirds
        illustration="document-folder-board-edit"
        title={i18n.t('trainings.training_settings.training_requests.title')}
        description={i18n.t('trainings.training_settings.training_requests.description')}
      >
        <Box width="full" gap="s32" alignItems="flexEnd">
          <Box
            gap="s12"
            paddingY="s16"
            paddingX="s24"
            border={{
              all: {
                color: 'grey400',
                width: 's1',
                style: 'solid',
              },
            }}
            borderRadius={{ all: 'abs008' }}
            maxWidth="s600"
          >
            <Box width="full" flexDirection="row" justifyContent="spaceBetween">
              <Box gap="s8">
                <Box flexDirection="row" justifyContent="spaceBetween">
                  <Text weight="semibold" size="300" color="grey1000">
                    {i18n.t('trainings.training_settings.training_requests.action')}
                  </Text>
                  <TogglerCheckbox
                    value={form.values.trainingRequestsEnabled}
                    onChange={(value) => {
                      form.setValues({ ...form.values, trainingRequestsEnabled: value })
                      form.handleSubmit()
                    }}
                    disabled={!manageSettings}
                  />
                </Box>
                <Text size="300" color="grey1000">
                  {i18n.t('trainings.training_settings.training_requests.box_description')}
                </Text>
              </Box>
            </Box>
          </Box>
          <Box onClick={() => setKey('trainings-settings-hint')}>
            <Hint.Popover
              step={2}
              title={i18n.t('trainings.training_settings.training_requests.hint_step_two.title')}
              description={i18n.t(
                'trainings.training_settings.training_requests.hint_step_two.description'
              )}
              actionLabel={i18n.t('common.close')}
              position="left bottom"
              isHidden={hidden}
            />
          </Box>
        </Box>
      </Section.TwoThirds>

      <ApprovalsBlock
        icon="document-approved"
        category="trainings"
        title={i18n.t('trainings.training_settings.approval_block.title')}
        description={i18n.t('trainings.training_settings.approval_block.description')}
      />
      <Box
        onClick={() => setKey('trainings-settings-hint')}
        position="relative"
        style={{ top: '-8%', left: '10%' }}
      >
        <Hint.Popover
          step={1}
          title={i18n.t('trainings.training_settings.training_requests.hint_step_one.title')}
          description={i18n.t(
            'trainings.training_settings.training_requests.hint_step_one.description'
          )}
          actionLabel={i18n.t('common.close')}
          position="bottom right"
          isHidden={hidden}
        />
      </Box>
      {company?.legalEntities?.some((entity) => entity.country === 'pt') && (
        <Section.TwoThirds
          illustration="checks"
          title={i18n.t('trainings.annex_c_relatorio_unico.settings_title')}
          description={i18n.t('trainings.annex_c_relatorio_unico.settings_description')}
        >
          <Box width="full" gap="s32" alignItems="flexEnd">
            <Box
              gap="s12"
              paddingY="s16"
              paddingX="s24"
              border={{
                all: {
                  color: 'grey400',
                  width: 's1',
                  style: 'solid',
                },
              }}
              borderRadius={{ all: 'abs008' }}
              minWidth="s600"
            >
              <Box width="full" flexDirection="row" justifyContent="spaceBetween">
                <Text weight="semibold" size="300" color="grey1000">
                  {i18n.t('trainings.annex_c_relatorio_unico.settings_enable')}
                </Text>
                <TogglerCheckbox
                  value={form.values.relatorioEnabled}
                  onChange={(value) => {
                    form.setValues({ ...form.values, relatorioEnabled: value })
                    form.handleSubmit()
                  }}
                  disabled={!manageSettings}
                />
              </Box>
            </Box>
          </Box>
        </Section.TwoThirds>
      )}

      {Boolean(session.company.legalEntities.find((x) => x.country === 'es')) && (
        <Section.TwoThirds
          illustration="document-approved"
          title={i18n.t('trainings.training_settings.fundae_subsidization.title')}
          description={i18n.t('trainings.training_settings.fundae_subsidization.description')}
        >
          <FundaeSubsidySection />
        </Section.TwoThirds>
      )}
    </Hint.Popover.Provider>
  )
}
